import React, { useEffect, useRef } from "react";
import { Control, Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import BaseInput from "@reusables/BaseInput";
import { Shield } from "lucide-react";
import { LineSchema } from "../../../../types";
import { useIsReservationsEnabled } from "@redux/features/companies/useIsReservationsEnabled";
import { AddProductFormSchemeTyping } from "@components/Dashboard/pages/Sales/Orders/components/MutationLayout/parts/ProductSection/modals/addProductDraftShared";

interface QuantityFieldsProps {
    control: Control<AddProductFormSchemeTyping>;
    selectedLine?: LineSchema;
}

/**
 * Type guard that checks if a value is a valid positive number.
 * 
 * Used to validate quantity values before performing sync/clamp operations.
 * Zero is explicitly excluded as it's treated the same as empty (no reservation intent).
 * 
 * @param value - The value to check
 * @returns True if value is a finite number greater than 0
 */
const isValidPositiveNumber = (value: number | undefined | null): value is number => 
    value !== undefined && value !== null && !isNaN(value) && value > 0;

/**
 * Converts a form field value to a number, handling empty states properly.
 * 
 * Form inputs can return empty strings, which `Number("")` converts to `0`.
 * This causes issues with our sync logic since we need to distinguish between
 * "user entered 0" and "field is empty". This utility treats empty/null/undefined
 * as `undefined` rather than `0`.
 * 
 * @param value - The raw form field value (could be string, number, null, undefined)
 * @returns The numeric value, or undefined if empty/invalid
 * 
 * @example
 * toNumberOrUndefined("")        // undefined (not 0!)
 * toNumberOrUndefined(undefined) // undefined
 * toNumberOrUndefined("12")      // 12
 * toNumberOrUndefined("2.5")     // 2.5
 * toNumberOrUndefined(0)         // 0
 */
const toNumberOrUndefined = (value: unknown): number | undefined => {
    if (value === undefined || value === null || value === "") return undefined;
    const num = Number(value);
    return isNaN(num) ? undefined : num;
};

/**
 * Quantity and Reserved Quantity input fields for the Add Product modal.
 * 
 * ## Motivation
 * Reservations are an uncommon feature - most orders won't need them. Therefore,
 * the reserved quantity field starts empty and doesn't auto-fill. However, when
 * a user does want to reserve stock, they often want to reserve the full order
 * quantity. This component provides smart assistance for that pattern while
 * staying out of the way for users who don't need reservations.
 * 
 * ## Behavior Patterns
 * 
 * ### 1. No Auto-Fill on Initial Entry
 * When the user enters a quantity, the reserved field stays empty.
 * This respects that reservations are opt-in.
 * 
 * ### 2. Full Reservation Sync
 * If the user sets reserved = quantity (indicating "reserve everything"),
 * and then changes the quantity, we automatically update reserved to match.
 * This predicts the user's intent to maintain full reservation.
 * 
 * **Example:**
 * - User enters quantity: 10, reserved: 10 (full reservation)
 * - User changes quantity to 15 → reserved auto-updates to 15
 * 
 * ### 3. Partial Reservation Independence
 * If reserved ≠ quantity, changes to quantity don't affect reserved.
 * The user explicitly chose a partial reservation amount.
 * 
 * **Example:**
 * - User enters quantity: 10, reserved: 5 (partial)
 * - User changes quantity to 15 → reserved stays at 5
 * 
 * ### 4. Upper Bound Enforcement
 * Reserved quantity cannot exceed order quantity. If the user enters
 * a reserved value higher than quantity, it's clamped down.
 * 
 * ### 5. Empty Quantity Resets State
 * When quantity is cleared, reserved is also cleared and tracking resets.
 * This ensures a fresh start when re-entering values.
 * 
 * ## Corner Cases
 * 
 * - **Empty strings:** Treated as undefined, not 0 (avoids false sync triggers)
 * - **Zero values:** Treated as invalid for sync purposes (no "0 = 0" sync)
 * - **Decimal values:** Fully supported via proper Number() conversion
 * - **Edit mode:** All auto-sync behavior is disabled when editing existing lines, and the reserved quantity field is visually removed
 * 
 * @param control - React Hook Form control object
 * @param selectedLine - If provided, indicates edit mode (disables auto-sync and visually removes the reserved quantity field)
 */
export default function QuantityFields({ control, selectedLine }: QuantityFieldsProps) {
    const { t } = useTranslation("", { keyPrefix: "sales.orders.general.orders.modals.addProduct" });
    const { setValue } = useFormContext<AddProductFormSchemeTyping>();

    // Check if reservations feature is enabled in company settings
    const isReservationsEnabled = useIsReservationsEnabled();

    const quantity = useWatch({ control, name: "quantity" });
    const reservedQuantity = useWatch({ control, name: "reserved_quantity" });

    // Track previous quantity to detect "full reservation" pattern
    const prevQuantityRef = useRef<number | undefined>(undefined);

    /**
     * Full Reservation Sync Effect
     * 
     * Monitors for the pattern where reserved === previous quantity,
     * indicating the user wants "full reservation". When quantity changes,
     * we update reserved to maintain this relationship.
     * 
     * Only runs when reservations feature is enabled.
     */
    useEffect(() => {
        // Skip sync logic if in edit mode or reservations feature is disabled
        if (selectedLine || !isReservationsEnabled) return;

        const quantityNum = toNumberOrUndefined(quantity);
        const reservedNum = toNumberOrUndefined(reservedQuantity);
        const prevQuantityNum = prevQuantityRef.current;

        // Sync reserved to new quantity only if user had "full reservation" (trying to predict the user's intent)
        const hadFullReservation = 
            isValidPositiveNumber(prevQuantityNum) &&
            isValidPositiveNumber(reservedNum) &&
            reservedNum === prevQuantityNum;
            
        const hasValidNewQuantity = isValidPositiveNumber(quantityNum);

        if (hadFullReservation && hasValidNewQuantity) {
            setValue("reserved_quantity", quantityNum);
        }

        // Update tracking ref when quantity changes to a valid value
        if (isValidPositiveNumber(quantityNum) && quantityNum !== prevQuantityNum) {
            prevQuantityRef.current = quantityNum;
        }
        
        // Reset tracking when quantity becomes empty/invalid (fresh start)
        if (!isValidPositiveNumber(quantityNum)) {
            prevQuantityRef.current = undefined;
        }
    }, [quantity, selectedLine, setValue, reservedQuantity, isReservationsEnabled]);

    /**
     * Upper Bound Enforcement Effect
     * 
     * Ensures reserved never exceeds quantity. Also clears reserved
     * when quantity is emptied to maintain consistent state.
     * 
     * Only runs when reservations feature is enabled.
     */
    useEffect(() => {
        // Skip enforcement if in edit mode or reservations feature is disabled
        if (selectedLine || !isReservationsEnabled) return;
        
        const reservedNum = toNumberOrUndefined(reservedQuantity);
        const quantityNum = toNumberOrUndefined(quantity);
        
        // Clear reserved when quantity becomes empty/invalid
        if (!isValidPositiveNumber(quantityNum) && isValidPositiveNumber(reservedNum)) {
            setValue("reserved_quantity", undefined);
            return;
        }
        
        // Clamp reserved down to quantity if it exceeds
        if (
            isValidPositiveNumber(reservedNum) && 
            isValidPositiveNumber(quantityNum) &&
            reservedNum > quantityNum
        ) {
            setValue("reserved_quantity", quantityNum);
        }
    }, [quantity, reservedQuantity, selectedLine, setValue, isReservationsEnabled]);

    return (
        <div className="flex flex-row gap-2 items-end">
            <Controller
                name="quantity"
                control={control}
                render={({ field, fieldState }) => (
                    <BaseInput
                        {...field}
                        error={fieldState.error}
                        label={t("fields.quantity.label")}
                        placeholder={t("fields.quantity.placeholder")}
                        type="number"
                        hideArrows
                    />
                )}
            />

            {/* Reserved quantity field - only shown when not in edit mode AND reservations feature is enabled */}
            {!selectedLine && isReservationsEnabled && (
                <div className="w-[250px]">
                    <Controller
                        name="reserved_quantity"
                        control={control}
                        render={({ field, fieldState }) => (
                            <BaseInput
                                {...field}
                                error={fieldState.error}
                                type="number"
                                icon={{
                                    right: {
                                        el: <Shield size={18} color="#A6DD4C" />,
                                        offset: "18px"
                                    }
                                }}
                                placeholder="Reserved"
                                hideArrows
                            />
                        )}
                    />
                </div>
            )}
        </div>
    );
}
